// /leads/:leadId/transcript?attempt= — ek call: recording, AI summary,
// transcript ka preview ("View" → poora modal me), aur neeche chipke
// duration / first contact / nateeja.
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./PounceLeadScreens.module.css";
import { Icon, LeadHero, ScreenShell } from "./LeadScreenParts";
import { firstContactSeconds, formatClock, formatDuration, initials, statusOf, useLeadDetail } from "../pounceLeadShared";

// Card me itni lines, baaki "View" ke modal me.
const PREVIEW_TURNS = 5;

// Retell ka plain transcript ("Agent: …\nUser: …") → turns. Line-wise waqt ke
// bina — tab chalta hai jab Retell se transcript_object na mile.
const parsePlainTranscript = (text) => {
  const turns = [];
  String(text || "")
    .split("\n")
    .forEach((line) => {
      const m = /^(Agent|User)\s*:\s*(.*)$/i.exec(line.trim());
      if (m) turns.push({ role: m[1].toLowerCase() === "agent" ? "agent" : "user", content: m[2], start: null });
      else if (line.trim() && turns.length) turns[turns.length - 1].content += ` ${line.trim()}`;
    });
  return turns;
};

// Summary paragraph → bullets (ek vaakya = ek bullet). Lookbehind regex nahi —
// purane iOS WebView pe wo poora bundle tod deta hai.
const toBullets = (summary) =>
  (String(summary || "").match(/[^.!?]+[.!?]*/g) || []).map((s) => s.trim()).filter(Boolean);

const PounceCallTranscript = () => {
  const { leadId } = useParams();
  const [params] = useSearchParams();
  const attemptId = params.get("attempt");
  const { lead, error, reload } = useLeadDetail(leadId, attemptId);
  const [showFull, setShowFull] = useState(false);

  // Modal khula ho to Esc se band, aur peeche ka page scroll na ho.
  useEffect(() => {
    if (!showFull) return undefined;
    const onKey = (e) => e.key === "Escape" && setShowFull(false);
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showFull]);

  return (
    <ScreenShell title="Call Transcript" lead={lead} error={error} reload={reload}>
      {(lead) => {
        const attempts = lead.attempts || [];
        const attempt =
          attempts.find((a) => String(a.id) === String(attemptId)) ||
          attempts.find((a) => a.id === lead.focusAttemptId);
        if (!attempt) {
          return (
            <>
              <LeadHero lead={lead} />
              <div className={styles.state}>
                <p className={styles.muted}>This call wasn't found.</p>
              </div>
            </>
          );
        }

        // Retell ke line-wise turns sirf usi call ke hain jiske liye server ne mangwaye.
        const turns =
          lead.focusAttemptId === attempt.id && lead.transcriptTurns
            ? lead.transcriptTurns
            : parsePlainTranscript(attempt.transcript);
        const callStart = attempt.startedAt ? new Date(attempt.startedAt).getTime() : null;
        const bullets = toBullets(attempt.callSummary);
        const secs = firstContactSeconds(lead);
        const outcome = lead.appointment ? "Appointment booked" : statusOf(lead).label;

        const renderTurn = (t, i) => {
          const at = callStart !== null && t.start !== null ? callStart + t.start * 1000 : null;
          const isAgent = t.role === "agent";
          return (
            <div key={i} className={styles.turn}>
              <span className={`${styles.turnAvatar} ${isAgent ? styles.turnAgent : styles.turnUser}`} aria-hidden="true">
                {isAgent ? "P" : lead.name ? initials(lead.name) : "L"}
              </span>
              <div className={styles.turnBody}>
                <p className={styles.turnWho}>
                  {isAgent ? "Outbound Caller" : lead.name || "Lead"}
                  {at && <time dateTime={new Date(at).toISOString()}>{formatClock(at, lead.displayTimezone)}</time>}
                </p>
                <p className={styles.turnText}>{t.content}</p>
              </div>
            </div>
          );
        };

        return (
          <>
            <LeadHero lead={lead} />

            {attempt.recordingUrl && (
              <section className={styles.card} aria-label="Call recording">
                <div className={styles.cardHead}>
                  {Icon.mic}
                  <h2 className={styles.cardTitle}>Call Recording</h2>
                  {attempt.durationSec > 0 && <span className={styles.cardAside}>{formatDuration(attempt.durationSec)}</span>}
                </div>
                <audio className={styles.audio} controls preload="none" src={attempt.recordingUrl}>
                  <a href={attempt.recordingUrl}>Download the recording</a>
                </audio>
              </section>
            )}

            {bullets.length > 0 && (
              <section className={styles.card} aria-label="AI summary">
                <div className={styles.cardHead}>
                  {Icon.spark}
                  <h2 className={styles.cardTitle}>AI Summary</h2>
                </div>
                <ul className={styles.bullets}>
                  {bullets.map((b, i) => (
                    <li key={i}>
                      {Icon.check}
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className={styles.card} aria-label="Transcript">
              <div className={styles.cardHead}>
                {Icon.file}
                <h2 className={styles.cardTitle}>Transcript</h2>
                {turns.length > 0 && (
                  <button type="button" className={styles.viewBtn} onClick={() => setShowFull(true)}>
                    View
                  </button>
                )}
              </div>
              {turns.length ? (
                <>
                  {turns.slice(0, PREVIEW_TURNS).map(renderTurn)}
                  {turns.length > PREVIEW_TURNS && (
                    <button type="button" className={styles.linkBtn} onClick={() => setShowFull(true)}>
                      View full transcript ({turns.length} lines)
                    </button>
                  )}
                </>
              ) : (
                <p className={styles.muted}>No transcript for this call.</p>
              )}
            </section>

            {/* Neeche chipka hua — modal khulne pe chhup jaata hai. */}
            {!showFull && (
              <section className={`${styles.stats} ${styles.statsBar}`} aria-label="Call stats">
                <div className={styles.stat}>
                  {Icon.clock}
                  <span className={styles.statText}>
                    <span className={styles.statLabel}>Call duration</span>
                    <span className={styles.statValue}>{formatDuration(attempt.durationSec) || "—"}</span>
                  </span>
                </div>
                <div className={styles.stat}>
                  {Icon.bolt}
                  <span className={styles.statText}>
                    <span className={styles.statLabel}>First contact in</span>
                    <span className={styles.statValue}>{secs !== null ? `${secs.toFixed(1)} sec` : "—"}</span>
                  </span>
                </div>
                <div className={styles.stat}>
                  <span className={lead.appointment ? styles.statOk : undefined}>{Icon.check}</span>
                  <span className={styles.statText}>
                    <span className={styles.statLabel}>Outcome</span>
                    <span className={styles.statValue}>{outcome}</span>
                  </span>
                </div>
              </section>
            )}

            {showFull && (
              <div className={styles.sheetBackdrop} onClick={() => setShowFull(false)}>
                <div
                  className={styles.sheet}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Full transcript"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.sheetHead}>
                    <h2 className={styles.cardTitle}>Transcript</h2>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => setShowFull(false)}
                      aria-label="Close transcript"
                      autoFocus
                    >
                      {Icon.close}
                    </button>
                  </div>
                  <div className={styles.sheetBody}>{turns.map(renderTurn)}</div>
                </div>
              </div>
            )}
          </>
        );
      }}
    </ScreenShell>
  );
};

export default PounceCallTranscript;
